// Native Android notification service bridge.
// Starts a foreground service that polls for notifications even when the app is backgrounded/killed.
// `window.Capacitor` is injected by @capacitor/core even in the browser build, so the
// correct native check is `isNativePlatform()` — otherwise registerPlugin proxies throw
// "plugin is not implemented on web" when any method is invoked.
const isNative = !!(window as any).Capacitor?.isNativePlatform?.();

// The plugin handle is kept BOXED. registerPlugin returns a Proxy that turns any
// property access into a native call, so the moment it sits on an await path the
// promise machinery probes `.then` on it and the proxy forwards that to native:
//   Uncaught (in promise) Error: "NotificationBridge.then()" is not implemented on android
// That threw on every launch, was swallowed by a catch, and left the handle null —
// so startService() returned early and the foreground poll service never started.
interface PluginBox { p: any }

let pluginPromise: Promise<PluginBox | null> | null = null;

function getPluginBox(): Promise<PluginBox | null> {
    if (!isNative) return Promise.resolve(null);
    if (!pluginPromise) {
        pluginPromise = (async () => {
            try {
                const { registerPlugin } = await import('@capacitor/core');
                return { p: registerPlugin('NotificationBridge') };
            } catch (e) {
                console.error('[NotifBridge] Could not register plugin:', e);
                return null;
            }
        })();
    }
    return pluginPromise;
}

export async function startNotificationService(token: string) {
    // Awaiting the handle here (rather than caching it into a module variable on
    // load) also removes a race: a login early in startup used to find the
    // handle still unset and give up silently.
    const box = await getPluginBox();
    if (!box) return;
    try {
        // Must be an absolute, device-reachable URL — the native service polls
        // it in the background. Default to production (never localhost) so a
        // missing REACT_APP_API_URL can't silently break notifications.
        const apiUrl = process.env.REACT_APP_API_URL || 'https://cloud.abstechnologies.co.in/api';
        await box.p.startService({ token, apiUrl });
        console.log('[NotifBridge] Foreground service started');
    } catch (e) {
        console.error('[NotifBridge] Failed to start service:', e);
    }
}

export async function stopNotificationService() {
    const box = await getPluginBox();
    if (!box) return;
    try {
        await box.p.stopService();
        console.log('[NotifBridge] Foreground service stopped');
    } catch (e) {
        console.error('[NotifBridge] Failed to stop service:', e);
    }
}

// Listen for notification taps from native side → navigate
if (typeof window !== 'undefined') {
    window.addEventListener('notificationNav', (e: any) => {
        const url = e.detail;
        if (url && window.location.pathname !== url) {
            window.location.href = url;
        }
    });
}
